import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  Boxes,
  Check,
  DollarSign,
  Handshake,
  MapPinned,
  MessageSquareText,
  Phone,
  Recycle,
  Truck,
  Zap,
} from "lucide-react";
import Seo from "../components/Seo";
import { CTA } from "../components/CTA";
import { BUSINESS, IMAGES } from "../lib/site";
import { EQUIPMENT_GRID, SERVICES, TICKER_ITEMS, WHY_SELL, WIRE_LIST } from "../data/content";

const WHY_ICONS = { DollarSign, MapPinned, Boxes, Truck, Handshake, Recycle };

const HeroButton = ({ to, href, children, variant = "solid", testId }) => {
  const cls =
    variant === "solid"
      ? "bg-ves-green text-ves-black hover:bg-white"
      : "border-2 border-ves-green text-ves-green hover:bg-ves-green hover:text-ves-black";
  const inner = (
    <span className={`flex items-center justify-center gap-2 px-7 py-4 font-heading text-base font-black tracking-wide transition-colors duration-150 ${cls}`}>
      {children}
    </span>
  );
  return to ? (
    <Link to={to} data-testid={testId}>{inner}</Link>
  ) : (
    <a href={href} data-testid={testId}>{inner}</a>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="We Buy Electrical Surplus Nationwide | Velocity Electrical Surplus"
        description="Velocity Electrical Surplus buys circuit breakers, electrical wire, bus plugs, disconnects, switchgear, transformers and complete industrial inventories nationwide. Cash paid. Call (586) 718-3404."
      />

      {/* HERO */}
      <section className="relative flex min-h-[88vh] items-center" data-testid="hero-section">
        <img
          src={IMAGES.hero}
          alt="Industrial electrical warehouse with switchgear, wire reels and electrical equipment"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ves-black via-ves-black/85 to-ves-black/40" />
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-up">
            <p className="mb-4 inline-block border border-ves-green/50 bg-ves-black/60 px-4 py-1.5 font-heading text-xs font-bold tracking-[0.25em] text-ves-green">
              NATIONWIDE ELECTRICAL SURPLUS BUYERS
            </p>
            <h1 className="font-heading text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
              WE BUY ELECTRICAL SURPLUS{" "}
              <span className="text-ves-green">NATIONWIDE</span>
            </h1>
            <p className="mt-5 font-heading text-sm font-bold tracking-[0.18em] text-ves-green sm:text-base">
              CIRCUIT BREAKERS • ELECTRICAL WIRE • BUS PLUGS • SWITCHGEAR • TRANSFORMERS
            </p>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
              Velocity Electrical Surplus specializes in purchasing new, used, obsolete, and
              surplus industrial electrical equipment nationwide — from individual pallets of
              circuit breakers and electrical wire to complete warehouses of industrial
              electrical inventory. Competitive offers, fast decisions, and a simple,
              straightforward selling process.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <HeroButton to="/sell-your-surplus" testId="hero-get-offer-btn">
                GET AN OFFER <ArrowRight className="h-5 w-5" />
              </HeroButton>
              <HeroButton href={BUSINESS.phoneTel} variant="outline" testId="hero-call-btn">
                <Phone className="h-5 w-5" /> CALL {BUSINESS.phoneDisplay}
              </HeroButton>
              <HeroButton href={BUSINESS.sms} variant="outline" testId="hero-text-btn">
                <MessageSquareText className="h-5 w-5" /> TEXT PHOTOS
              </HeroButton>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-bold tracking-wider text-white/70">
              {["CASH PAID", "ALL 50 STATES", "WE COME TO YOU", "FREIGHT ARRANGED"].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-ves-green" /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="overflow-hidden border-y-4 border-ves-black bg-ves-green py-3" aria-hidden="true">
        <div className="flex w-max animate-marquee gap-8">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center gap-8 font-heading text-sm font-black tracking-widest text-ves-black">
              {item} <span className="text-ves-black/50">///</span>
            </span>
          ))}
        </div>
      </div>

      {/* WIRE FEATURE */}
      <section className="bg-ves-black py-20 sm:py-28" data-testid="wire-feature-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="relative">
            <img
              src={IMAGES.wireReel}
              alt="Industrial electrical wire reels and copper cable spools"
              className="w-full border border-white/10 object-cover hard-shadow"
              loading="lazy"
            />
            <div className="absolute -bottom-5 -left-2 bg-ves-green px-5 py-3 font-heading text-sm font-black tracking-wide text-ves-black sm:-left-5">
              TOP DOLLAR FOR REUSABLE WIRE
            </div>
          </div>
          <div>
            <p className="font-heading text-xs font-bold tracking-[0.25em] text-ves-green">
              BEFORE YOU SCRAP IT
            </p>
            <h2 className="mt-3 font-heading text-3xl font-black leading-tight text-white sm:text-4xl text-balance">
              DON&rsquo;T SCRAP YOUR ELECTRICAL WIRE BEFORE GETTING OUR OFFER.
            </h2>
            <p className="mt-3 font-heading text-lg font-bold text-ves-green">
              YOUR SURPLUS ELECTRICAL WIRE MAY BE WORTH MORE THAN SCRAP.
            </p>
            <p className="mt-5 text-base leading-relaxed text-white/75">
              Many electrical contractors and industrial facilities sell valuable wire for its
              copper content without considering whether the material has additional value as
              reusable electrical inventory. We evaluate qualifying material for its potential
              reuse and resale value — helping you recover more value while reducing
              unnecessary waste.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {WIRE_LIST.slice(0, 8).map((w) => (
                <span key={w} className="border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80">
                  {w}
                </span>
              ))}
            </div>
            <div className="mt-6 border-l-4 border-ves-green bg-ves-forest/60 p-5">
              <p className="font-heading text-base font-black tracking-wide text-white">
                STOP SELLING VALUABLE WIRE FOR SCRAP PRICES.
              </p>
              <p className="mt-1 text-sm text-white/80">
                LET VELOCITY ELECTRICAL SURPLUS EVALUATE IT FIRST.
              </p>
            </div>
            <Link
              to="/electrical-wire-cable"
              className="mt-6 inline-flex items-center gap-2 font-heading text-sm font-black tracking-wide text-ves-green transition-colors duration-150 hover:text-white"
              data-testid="wire-feature-link"
            >
              SEE ALL WIRE &amp; CABLE WE BUY <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* EQUIPMENT GRID */}
      <section className="bg-ves-charcoal/40 py-20 sm:py-28" data-testid="equipment-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-heading text-xs font-bold tracking-[0.25em] text-ves-green">
              WHAT WE PURCHASE
            </p>
            <h2 className="mt-3 font-heading text-3xl font-black text-white sm:text-4xl">
              ELECTRICAL EQUIPMENT WE BUY
            </h2>
            <p className="mt-4 text-base text-white/70">
              New, used, surplus, discontinued and obsolete — we purchase individual lots and
              complete industrial electrical inventories.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {EQUIPMENT_GRID.map((card, i) => (
              <div
                key={card.title}
                className={`group flex flex-col border border-white/10 bg-ves-black transition-all duration-150 hover:-translate-y-1 hover:border-ves-green/60 ${
                  i === 0 || i === 7 ? "sm:col-span-2" : ""
                }`}
                data-testid={`equipment-card-${i}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={card.img}
                    alt={card.title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ves-black/80 to-transparent" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-heading text-lg font-black text-white">{card.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-white/65">{card.desc}</p>
                  <div className="mt-4 flex items-center gap-4">
                    <Link
                      to={`/sell-your-surplus?category=${encodeURIComponent(card.category)}`}
                      className="bg-ves-green px-4 py-2 font-heading text-xs font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white"
                      data-testid={`equipment-sell-btn-${i}`}
                    >
                      SELL THIS EQUIPMENT
                    </Link>
                    {card.link && (
                      <Link
                        to={card.link}
                        className="text-xs font-bold text-white/60 transition-colors duration-150 hover:text-ves-green"
                        data-testid={`equipment-details-link-${i}`}
                      >
                        Details →
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES STRIP */}
      <section className="border-y border-white/10 bg-ves-forest/40 py-14" data-testid="services-strip">
        <div className="mx-auto grid max-w-7xl gap-x-8 gap-y-4 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-3 lg:px-8">
          {SERVICES.map((s) => (
            <div key={s} className="flex items-center gap-3">
              <BadgeDollarSign className="h-5 w-5 shrink-0 text-ves-green" />
              <span className="font-heading text-sm font-bold tracking-wide text-white">{s}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHY SELL */}
      <section className="bg-ves-black py-20 sm:py-28" data-testid="why-sell-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-heading text-xs font-bold tracking-[0.25em] text-ves-green">
              THE VELOCITY DIFFERENCE
            </p>
            <h2 className="mt-3 font-heading text-3xl font-black text-white sm:text-4xl">
              WHY SELL TO VELOCITY ELECTRICAL SURPLUS?
            </h2>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_SELL.map((item, i) => {
              const Icon = WHY_ICONS[item.icon];
              return (
                <div
                  key={item.title}
                  className="border border-white/10 bg-ves-charcoal/60 p-6 transition-all duration-150 hover:-translate-y-1 hover:border-ves-green/60"
                  data-testid={`why-card-${i}`}
                >
                  <Icon className="h-8 w-8 text-ves-green" />
                  <h3 className="mt-4 font-heading text-lg font-black text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/65">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-y border-white/10 bg-white py-20 text-ves-black sm:py-24" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-black sm:text-4xl">
            SELLING YOUR SURPLUS IS SIMPLE
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { n: "01", t: "Send Pictures or a List", d: "Call, text, or upload photos of your equipment or wire through our offer form." },
              { n: "02", t: "Receive an Offer", d: "We evaluate condition, quantity, market demand and reuse value — same-day offers when possible." },
              { n: "03", t: "Get Paid", d: "We arrange freight and loading for qualifying purchases — payment before equipment is loaded, when agreed." },
            ].map((s) => (
              <div key={s.n} className="border-t-4 border-ves-green pt-5" data-testid={`process-step-${s.n}`}>
                <span className="font-heading text-4xl font-black text-ves-green">{s.n}</span>
                <h3 className="mt-2 font-heading text-xl font-black">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ves-charcoal/80">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIQUIDATION BANNER */}
      <section className="relative py-20 sm:py-28" data-testid="liquidation-banner">
        <img
          src={IMAGES.scrapCopper}
          alt="Industrial copper material recovery"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-ves-black/85" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="max-w-3xl font-heading text-3xl font-black text-white sm:text-4xl text-balance">
            CLOSING A BUSINESS? CLEARING A WAREHOUSE? UPGRADING YOUR FACILITY?
          </h2>
          <p className="mt-4 max-w-2xl text-base text-white/75">
            Velocity Electrical Surplus purchases complete electrical inventories and
            industrial surplus lots — circuit breakers, electrical wire, industrial equipment,
            and related material. No sorting necessary.
          </p>
          <ul className="mt-6 grid max-w-2xl gap-2 text-sm text-white/80 sm:grid-cols-2">
            {["Electrical Contractors", "Manufacturing Plants", "Demolition Contractors", "Warehouses", "Maintenance Departments", "Businesses Closing or Relocating"].map((x) => (
              <li key={x} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-ves-green" /> {x}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/warehouse-liquidations"
              className="bg-ves-green px-7 py-4 text-center font-heading text-base font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white"
              data-testid="liquidation-learn-more-btn"
            >
              COMPLETE BUYOUTS
            </Link>
            <a
              href={BUSINESS.phoneTel}
              className="border-2 border-ves-green px-7 py-4 text-center font-heading text-base font-black tracking-wide text-ves-green transition-colors duration-150 hover:bg-ves-green hover:text-ves-black"
              data-testid="liquidation-call-btn"
            >
              CALL {BUSINESS.manager.toUpperCase()} — {BUSINESS.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      <CTA
        headline="HAVE ELECTRICAL SURPLUS? TURN IT INTO CASH TODAY!"
        sub="Nationwide buyers. Fast offers. Cash paid. Call or text Velocity Electrical Surplus now."
      />
    </div>
  );
}
